// The geometry of the two lockups of the brand art (brand.md §2, §7, §8):
// the header lockup of card 16 and the stacked lockup of card 17, with the
// fitted tag line of the platform images. Every length is a fraction of
// the sign box and comes from `[presentation.lockup]` through `LOCKUP`.
//
// The module is pure: a `Measure` gives the advance of a character, so a
// test sets a line with the face's own advance and the page sets it with
// the canvas. `paint.ts` paints what this module places.

import { LOCKUP } from '../gen/presentation';
import { SIGN_BOX, SIGN_CENTRE, signGeometry } from '../ui/sign';

/** The name of the game, as every lockup sets it. */
export const NAME = 'AQUAPHOBIA';

/** The advance of one character at the type size `px`, in px. */
export type Measure = (ch: string, px: number) => number;

/** One line of caps, set: the left edge of each character, and the width
 *  with no tracking after the last letter (brand.md §2.1). */
export interface SetLine {
  readonly text: string;
  /** The cap height, in px. */
  readonly cap: number;
  /** The type size: the cap height over the cap height of the face. */
  readonly px: number;
  /** The tracking, in px. */
  readonly track: number;
  readonly xs: readonly number[];
  readonly width: number;
}

/** Sets `text` in the lockup face at the cap height `cap`. */
export function setLine(text: string, cap: number, trackEm: number, measure: Measure): SetLine {
  const px = cap / LOCKUP.faceCapEm;
  const track = trackEm * px;
  const xs: number[] = [];
  let x = 0;
  for (const ch of text) {
    xs.push(x);
    x += measure(ch, px) + track;
  }
  return { text, cap, px, track, xs, width: xs.length > 0 ? x - track : 0 };
}

/** Sets `text` at the cap height `cap`; a line wider than `maxW` takes
 *  the smaller cap height that makes it exactly `maxW` wide. */
export function setLineWithin(text: string, cap: number, trackEm: number, maxW: number, measure: Measure): SetLine {
  const one = setLine(text, cap, trackEm, measure);
  return one.width <= maxW ? one : setLine(text, (cap * maxW) / one.width, trackEm, measure);
}

/** Sets `text` to exactly `width`: the tracking fills the width. A line
 *  that would need less than `LOCKUP.tag.minTrackEm` takes that tracking
 *  and a smaller cap height instead, so it still ends at `width`. */
export function fitLine(text: string, cap: number, width: number, measure: Measure): SetLine {
  const bare = setLine(text, cap, 0, measure);
  const gaps = bare.xs.length - 1;
  if (gaps < 1) return bare;
  const track = (width - bare.width) / gaps;
  if (track >= LOCKUP.tag.minTrackEm * bare.px) return setLine(text, cap, track / bare.px, measure);
  const least = bare.width + gaps * LOCKUP.tag.minTrackEm * bare.px;
  return setLine(text, (cap * width) / least, LOCKUP.tag.minTrackEm, measure);
}

/** A placed line: its left edge and its base line, in the lockup box. */
export interface LinePlace {
  readonly line: SetLine;
  readonly x: number;
  readonly base: number;
}

/** One lockup, laid out in its own box: the origin is the top left of the
 *  lockup with no clear space. */
export interface LockupGeom {
  readonly kind: 'header' | 'stacked';
  /** The side of the sign box, and its top left. */
  readonly sign: number;
  readonly signX: number;
  readonly signY: number;
  readonly name: LinePlace;
  readonly tag: LinePlace | null;
  /** The DEV line of the dev identity, under the name or the tag line
   *  (brand.md §15.1), or null. */
  readonly dev: LinePlace | null;
  /** The box of the lockup, with no clear space. */
  readonly w: number;
  readonly h: number;
  /** The clear space on every side, in px. */
  readonly clear: number;
}

/** The header lockup of card 16 at the sign box `h`, with the fitted tag
 *  line under the name when `tag` is given (brand.md §2.1, §7). */
export function headerLockup(h: number, measure: Measure, tag?: string): LockupGeom {
  const cap = LOCKUP.cap * h;
  const line = setLine(NAME, cap, LOCKUP.trackEm, measure);
  const x = h + LOCKUP.gap * h;
  // The middle of the cap height is the middle of the sign box.
  const base = h / 2 + cap / 2;
  let tagPlace: LinePlace | null = null;
  if (tag) {
    const t = fitLine(tag, cap * LOCKUP.tag.cap, line.width, measure);
    tagPlace = { line: t, x, base: base + cap * LOCKUP.tag.gap + t.cap };
  }
  return {
    kind: 'header', sign: h, signX: 0, signY: 0,
    name: { line, x, base }, tag: tagPlace, dev: null,
    w: x + line.width, h: Math.max(h, tagPlace ? tagPlace.base : 0),
    clear: LOCKUP.clear * h,
  };
}

/** The stacked lockup of card 17 at the sign box `b`: the sign above the
 *  name, both centred, with the fitted tag line under the name when `tag`
 *  is given (brand.md §8). `dev` sets the DEV line of the dev identity
 *  under the last line: under the name at `LOCKUP.dev.cap` of its cap
 *  height, or under the tag line at `LOCKUP.dev.tagCap` of the cap height
 *  of the tag line (brand.md §15.1). */
export function stackedLockup(b: number, measure: Measure, tag?: string, dev = false): LockupGeom {
  const S = LOCKUP.stacked;
  const D = LOCKUP.dev;
  const cap = S.cap * b;
  const line = setLine(NAME, cap, LOCKUP.trackEm, measure);
  const base = b + S.gap * b + cap;
  const t = tag ? fitLine(tag, cap * LOCKUP.tag.cap, line.width, measure) : null;
  const tagBase = t ? base + cap * LOCKUP.tag.stackedGap + t.cap : base;
  const d = dev ? setLine(D.word, t ? t.cap * D.tagCap : cap * D.cap, D.trackEm, measure) : null;
  const w = Math.max(b, line.width, t ? t.width : 0, d ? d.width : 0);
  const tagPlace = t ? { line: t, x: (w - t.width) / 2, base: tagBase } : null;
  const devPlace = d ? { line: d, x: (w - d.width) / 2, base: tagBase + cap * D.gap + d.cap } : null;
  return {
    kind: 'stacked', sign: b, signX: (w - b) / 2, signY: 0,
    name: { line, x: (w - line.width) / 2, base }, tag: tagPlace, dev: devPlace,
    w, h: devPlace ? devPlace.base : tagBase,
    clear: S.clear * b,
  };
}

/** A box the check holds inside the guides of a picture. */
export interface CheckBox {
  readonly what: 'sign' | 'name' | 'tag' | 'dev' | 'season' | 'slot';
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The inset of the cell of the sign in its box: the cell is narrower than
 *  the box, and it nearly fills the box from top to foot. */
function cellInset(): { x: number; y: number } {
  const g = signGeometry('full', 'round');
  return {
    x: (SIGN_CENTRE - (g.r * Math.sqrt(3)) / 2) / SIGN_BOX,
    y: (SIGN_CENTRE - g.r) / SIGN_BOX,
  };
}

/** The check box of the cell of a sign whose box of the side `s` has its
 *  top left at (`x`, `y`). */
export function signCellBox(s: number, x: number, y: number): CheckBox {
  const i = cellInset();
  return { what: 'sign', x: x + i.x * s, y: y + i.y * s, w: s * (1 - 2 * i.x), h: s * (1 - 2 * i.y) };
}

/** The boxes of a lockup placed with its origin at (`x`, `y`): the cell of
 *  the sign, the cap height of the name, and the cap height of the tag. */
export function lockupBoxes(g: LockupGeom, x: number, y: number): CheckBox[] {
  const capBox = (what: CheckBox['what'], p: LinePlace): CheckBox =>
    ({ what, x: x + p.x, y: y + p.base - p.line.cap, w: p.line.width, h: p.line.cap });
  const out: CheckBox[] = [signCellBox(g.sign, x + g.signX, y + g.signY), capBox('name', g.name)];
  if (g.tag) out.push(capBox('tag', g.tag));
  if (g.dev) out.push(capBox('dev', g.dev));
  return out;
}

/** The top of the visible block of a lockup: the top of the cell. */
export function visibleTop(g: LockupGeom): number {
  return g.signY + cellInset().y * g.sign;
}
